import { buildQueryString } from '@shared/hooks/helpers/QueryString';
import TokenResponse from '@shared/models/authentication/TokenResponse';
import UpdateAuthClientRequest from '@shared/models/authentication/UpdateAuthClientRequest';
import { CameraModelReferenceModel } from '@shared/models/controls/CameraModelReferenceModel';
import { VoiceModelReferenceModel } from '@shared/models/controls/VoiceModelReferenceModel';
import DeviceProfileCreateUpdateModel from '@shared/models/deviceProfiles/DeviceProfileCreateUpdateModel';
import DeviceProfilesFilterRequest from '@shared/models/deviceProfiles/DeviceProfilesFilterRequest';
import DeviceProfileShareModel from '@shared/models/deviceProfiles/DeviceProfileShareModel';
import DeviceProfileSharePreviewModel from '@shared/models/deviceProfiles/DeviceProfileSharePreviewModel';
import GenerateProfileRequest from '@shared/models/deviceProfiles/GenerateProfileRequest';
import MacroModel from '@shared/models/deviceProfiles/MacroModel';
import ShareDeviceProfileRequest from '@shared/models/deviceProfiles/ShareDeviceProfileRequest';
import UserDeviceProfileModel from '@shared/models/deviceProfiles/UserDeviceProfileModel';
import CreateDeviceTokenRequestOptions from '@shared/models/devices/CreateDeviceTokenRequestOptions';
import { CreateDeviceTypeClientListingRequest } from '@shared/models/devices/CreateDeviceTypeClientListingRequest';
import { CreateDeviceTypeCustomControlsRequest } from '@shared/models/devices/CreateDeviceTypeCustomControlsRequest';
import { CustomControlModel } from '@shared/models/devices/CustomControlModel';
import { CustomControlsModel } from '@shared/models/devices/CustomControlsModel';
import DeviceTypeClientListingModel from '@shared/models/devices/DeviceTypeClientListingModel';
import DeviceTypeModel from '@shared/models/devices/DeviceTypeModel';
import { UpdateDeviceTypeCustomControlsRequest } from '@shared/models/devices/UpdateDeviceTypeCustomControlsRequest';
import UpdateDeviceTypeListingsModel from '@shared/models/devices/UpdateDeviceTypeListingsModel';
import UpdateDeviceTypeRequest from '@shared/models/devices/UpdateDeviceTypeRequest';
import UpdateUserDeviceRequest from '@shared/models/devices/UpdateUserDeviceRequest';
import UserDeviceCustomControlOverrideModel from '@shared/models/devices/UserDeviceCustomControlOverrideModel';
import UserDeviceModel from '@shared/models/devices/UserDeviceModel';
import UserDeviceTokenModel from '@shared/models/devices/UserDeviceTokenModel';
import ISearchParams from '@shared/models/ISearchParams';
import InviteOrganizationMemberRequest from '@shared/models/organizations/InviteOrganizationMemberRequest';
import { LicenseKeyCheckResponse } from '@shared/models/organizations/LicenseKeyCheckResponse';
import LicenseKeyModel from '@shared/models/organizations/LicenseKeyModel';
import OrganizationAuthClientModel from '@shared/models/organizations/OrganizationAuthClientModel';
import OrganizationMemberDetailsModel from '@shared/models/organizations/OrganizationMemberDetailsModel';
import OrganizationMemberInviteModel from '@shared/models/organizations/OrganizationMemberInviteModel';
import OrganizationMemberModel from '@shared/models/organizations/OrganizationMemberModel';
import OrganizationModel from '@shared/models/organizations/OrganizationModel';
import OrganizationPrivateProductModel from '@shared/models/organizations/OrganizationPrivateProductModel';
import { UpdateOrganizationMemberRequest } from '@shared/models/organizations/UpdateOrganizationMemberRequest';
import UpdateOrganizationRequest from '@shared/models/organizations/UpdateOrganizationRequest';
import { ProductModel } from '@shared/models/product/ProductModel';
import ScopeModel from '@shared/models/scopes/ScopeModel';
import CapabilityFacetGroupModel from '@shared/models/users/CapabilityFacetGroupModel';
import ChangePasswordRequest from '@shared/models/users/ChangePasswordRequest';
import INotificationModel from '@shared/models/users/NotificationModel';
import OnboardingStatusModel from '@shared/models/users/OnboardingStatusModel';
import UpdateEmailPreferencesRequest from '@shared/models/users/UpdateEmailPreferencesRequest';
import UpdateUserCapabilitiesRequest from '@shared/models/users/UpdateUserCapabilitiesRequest';
import UpdateUserLocaleRequest from '@shared/models/users/UpdateUserLocaleRequest';
import { UserAppInterfaceControlOverride } from '@shared/models/users/UserAppInterfaceControlOverride';
import UserLicenseAccessModel from '@shared/models/users/UserLicenseAccessModel';
import UserModel from '@shared/models/users/UserModel';
import UserPreferencesModel from '@shared/models/users/UserPreferencesModel';
import UserWithCapabilitiesModel from '@shared/models/users/UserWithCapabilitiesModel';
import { sendDelete, sendGet, sendPost, sendPut, setCommandToken } from './generic';

export const refreshToken = (clientId: string, clientSecret: string, refreshToken: string) => {
    return sendPost<TokenResponse>(
        `/signin/token?client_id=${clientId}&client_secret=${clientSecret}&refresh_token=${refreshToken}&grant_type=refresh_token`,
        null
    );
};

export const getToken = (clientId: string, code: string, redirectUri: string) => {
    return sendPost<TokenResponse>(
        `/signin/token?client_id=${clientId}&code=${code}&redirect_uri=${encodeURI(redirectUri)}&grant_type=code`,
        null
    );
};

export const validateUserAuthClientPermission = () => {
    return sendGet<UserModel>('/api/Authentication/AuthClient/FeatureAccess');
};

export const getOnboardingStatus = () => {
    return sendGet<OnboardingStatusModel>(`/api/ClientOnboarding/status/current`);
};

export const setOnboardingStatus = (request: UpdateUserOnboardingStatusRequest) => {
    return sendPut<OnboardingStatusModel>(`/api/ClientOnboarding/status/current`, request);
};

export const registerNewDevice = (deviceTypeId: string, name?: string, hardwareId?: string, platform?: string) => {
    let paramsObj = {} as any;
    if (name) {
        paramsObj.name = name;
    }
    if (hardwareId) {
        paramsObj.hardwareId = hardwareId;
    }
    if (platform) {
        paramsObj.platform = platform;
    }
    const params = new URLSearchParams(paramsObj);
    const queryString = params.toString();
    return sendPost<UserDeviceModel>(`/api/device/userDevices/new/${deviceTypeId}?${queryString ?? ''}`, null);
};

export const getUserDevices = () => {
    return sendGet<UserDeviceModel[]>(`/api/device/userDevices`);
};

export const getDeviceTypes = () => {
    return sendGet<DeviceTypeModel[]>(`/api/device/deviceTypes`);
};

export const getDeviceTypesPublicClientListingLocale = (locale: string) => {
    return sendGet<DeviceTypeClientListingModel[]>(
        `/api/Device/deviceTypes/public/clientListings/locale?locale=${locale}`
    );
};

export const getDeviceTypeClientListing = (deviceTypeId: string, locale: string) => {
    return sendGet<DeviceTypeClientListingModel>(
        `/api/device/deviceTypes/${deviceTypeId}/clientListing?locale=${locale}`
    );
};

export const getDeviceTypeCustomControls = (deviceTypeId: string, locale: string) => {
    return sendGet<CustomControlsModel>(`/api/device/deviceTypes/${deviceTypeId}/customControls?locale=${locale}`);
};

export const getUserDevice = (userDeviceId: string) => {
    return sendGet<UserDeviceModel>(`/api/device/userDevices/${userDeviceId}`);
};

export const getUserDeviceToken = (userDeviceId: string) => {
    return sendGet<UserDeviceTokenModel[]>(`/api/device/userDevices/${userDeviceId}/tokens`);
};

export const generateUserDeviceToken = (userDeviceId: string, options: CreateDeviceTokenRequestOptions) => {
    return sendPost<UserDeviceTokenModel>(`/api/device/userDevices/${userDeviceId}/tokens`, options);
};

export const getDeviceCustomControls = (userDeviceId: string, locale: string) => {
    return sendGet<CustomControlsModel>(`/api/device/userDevices/${userDeviceId}/customControls?locale=${locale}`);
};

export const updateDeviceCustomControls = (
    userDeviceTypeId: string,
    configuration: UserDeviceCustomControlOverrideModel[],
    locale: string
) => {
    return sendPut<CustomControlsModel>(
        `/api/device/userDevices/${userDeviceTypeId}/customControls?locale=${locale}`,
        configuration
    );
};
export const updateCustomUserDeviceControls = (
    userDeviceId: string,
    controls: CustomControlModel[],
    locale: string
) => {
    return sendPut<CustomControlsModel>(
        `/api/device/userDevices/${userDeviceId}/customControls/default?locale=${locale}`,
        controls
    );
};

export const generateCommandToken = (userDeviceId: string) => {
    return sendPost<UserDeviceTokenModel>(`/api/device/userDevices/${userDeviceId}/tokens/command`, null);
};

export const setDeviceProfile = (userDeviceId: string, profileId: string) => {
    return sendPut<UserDeviceModel>(`/api/device/userDevices/${userDeviceId}/setprofile/${profileId}`, null);
};

export const updateDeviceProfile = (userDeviceId: string, request: UpdateDeviceProfileRequest) => {
    return sendPut<UserDeviceModel>(`/api/device/userDevices/${userDeviceId}/setProfile/v2`, request);
};

export const getDeviceProfiles = () => {
    return sendGet<UserDeviceProfileModel[]>(`/api/deviceProfile/private`);
};

export const getDeviceProfile = (profileId: string) => {
    return sendGet<UserDeviceProfileModel>(`/api/deviceProfile/${profileId}`);
};

export const getPublicProfiles = (request: DeviceProfilesFilterRequest) => {
    const params = new URLSearchParams();

    // build the query string
    Object.entries(request).forEach(([key, value]) => {
        if (value == null) return;
        if (Array.isArray(value)) {
            value.forEach((item) => params.append(key, item));
        } else {
            params.append(key, value.toString());
        }
    });

    let queryString = params.toString();
    queryString = queryString ? `?${queryString}` : '';

    return sendGet<UserDeviceProfileModel[]>(`/api/deviceProfile/public${queryString}`);
};

export const getPublicProfileById = (profileId: string) => {
    return sendGet<UserDeviceProfileModel>(`/api/deviceProfile/public/${profileId}`);
};

export const createProfile = (configuration: DeviceProfileCreateUpdateModel) => {
    return sendPost<UserDeviceProfileModel>(`/api/deviceProfile/v2`, configuration);
};

export const updateProfile = (profileId: string, configuration: DeviceProfileCreateUpdateModel) => {
    return sendPut<UserDeviceProfileModel>(`/api/deviceProfile/v2/${profileId}`, configuration);
};

export const deleteProfile = (profileId: string) => {
    return sendDelete<UserDeviceProfileModel>(`/api/deviceProfile/${profileId}`);
};

export const startDeviceListening = (userDeviceId: string, token: string) => {
    setCommandToken(token);
    return sendPost<UserDeviceModel>(`/api/deviceCommand/${userDeviceId}/listen/start`, null);
};
export const stopDeviceListening = (userDeviceId: string, token: string) => {
    setCommandToken(token);
    return sendPost<UserDeviceModel>(`/api/deviceCommand/${userDeviceId}/listen/stop`, null);
};

export const updateUserDevice = (userDeviceId: string, model: UpdateUserDeviceRequest) => {
    return sendPut<UserDeviceModel>(`/api/device/userDevices/${userDeviceId}`, model);
};

export const unregisterUserDevice = (userDeviceId: string) => {
    return sendDelete<UserDeviceModel>(`/api/device/userDevices/${userDeviceId}`);
};

export const disableDevice = (userDeviceId: string) => {
    return sendPut<UserDeviceModel>(`/api/device/userDevices/${userDeviceId}/disable`, null);
};

export const sendDeviceCommand = (userDeviceId: string, command: string, token: string, macro?: MacroModel) => {
    setCommandToken(token);
    return sendPost(`/api/deviceCommand/${userDeviceId}/command?command=${command}`, macro);
};
export const sendDeviceTypeCommand = (userDeviceId: string, phrase: string, token: string) => {
    setCommandToken(token);
    return sendPost(`/api/deviceCommand/${userDeviceId}/type?phrase=${phrase}`, null);
};
export const updateDeviceFirmware = (userDeviceId: string, token: string) => {
    setCommandToken(token);
    return sendPost(`/api/deviceCommand/${userDeviceId}/firmware/update`, null);
};

export const getCurrentUser = () => {
    return sendGet<UserModel>(`/api/user`);
};

export const softDeleteCurrentUser = () => {
    return sendDelete<UserModel>(`/api/user/deleteCurrentUser`);
};

export const changePassword = (request: ChangePasswordRequest) => {
    return sendPut<any>(`/api/user/changePassword`, request);
};

export const updateUserLocales = (request: UpdateUserLocaleRequest) => {
    return sendPut<UserModel>(`/api/user/locales`, request);
};

export const resetUserLocales = () => {
    return sendPut<UserModel>(`/api/user/locales/reset`, null);
};

export const requestEmailVerification = () => {
    return sendPost<UserModel>(`/api/user/requestEmailVerification`, null);
};

export const getUserLicenseAccess = () => {
    return sendGet<UserLicenseAccessModel>(`/api/user/licenseAccess`);
};

export const shareProfile = (profileId: string, request: ShareDeviceProfileRequest) => {
    return sendPost<DeviceProfileShareModel>(`/api/deviceProfile/${profileId}/share`, request);
};

export const getProfileShares = (profileId: string) => {
    return sendGet<DeviceProfileShareModel[]>(`/api/deviceProfile/${profileId}/shares`);
};

export const disableProfileShare = (shareId: string) => {
    return sendPut<DeviceProfileShareModel>(`/api/deviceProfile/shares/${shareId}/disable`, null);
};

export const getProfileSharePreview = (shareAccessCode: string) => {
    return sendGet<DeviceProfileSharePreviewModel>(`/api/deviceProfile/shares/preview/${shareAccessCode}`);
};

export const acceptProfileShare = (shareAccessCode: string) => {
    return sendPost<UserDeviceProfileModel>(`/api/deviceProfile/shares/accept/${shareAccessCode}`, null);
};

export const getCapabilityFacets = () => {
    return sendGet<CapabilityFacetGroupModel[]>(`/api/capabilityFacets`);
};

export const getUserWithCapabilitiess = () => {
    return sendGet<UserWithCapabilitiesModel>(`/api/user/CapabilitySettings`);
};

export const updateUserCapabilities = (request: UpdateUserCapabilitiesRequest) => {
    return sendPut<UserWithCapabilitiesModel>(`/api/user/CapabilitySettings`, request);
};

export const generateUserDeviceProfile = (request: GenerateProfileRequest) => {
    return sendPost<UserDeviceProfileModel>(`/api/deviceProfile/generate`, request);
};

export const getUserNotificationsAll = () => {
    return sendGet<INotificationModel[]>(`/api/SystemNotifications/scoped/all`);
};

export const getUserNotificationsUnread = () => {
    return sendGet<INotificationModel[]>(`/api/SystemNotifications/scoped/unread`);
};

export const markNotificationRead = (notificationId: string) => {
    return sendPut<INotificationModel>(`/api/SystemNotifications/${notificationId}/read`, null);
};

export const generateNotification = (notification: INotificationModel) => {
    return sendPost<INotificationModel>(`/api/SystemNotifications/scoped`, notification);
};

export const updateUserEmailPreferences = (request: UpdateEmailPreferencesRequest) => {
    return sendPut<UserModel>(`/api/user/emailPreferences`, request);
};

export const updateUserPreferences = (request: UserPreferencesModel) => {
    return sendPut<UserModel>(`/api/user/UserPreferences`, request);
};

export const getUserPreferences = () => {
    return sendGet<UserPreferencesModel>('/api/user/UserPreferences');
};

export const getUserAppInterfaceControls = (locale: string) => {
    return sendGet<CustomControlsModel>(`/api/user/AppInterfaceControls?locale=${locale}`);
};

export const updateUserAppInterfaceControls = (interfaceControl: UserAppInterfaceControlOverride[], locale: string) => {
    return sendPut<CustomControlsModel>(`/api/user/AppInterfaceControls?locale=${locale}`, interfaceControl);
};

export const getScopes = () => {
    return sendGet<ScopeModel[]>(`/api/Scope`);
};

// organization endpoints
export const acceptOrganizationInvite = (inviteCode: string) => {
    return sendPost<OrganizationMemberModel>(`/api/organization/invitation/accept?inviteCode=${inviteCode}`, null);
};

export const rejectOrganizationInvite = (inviteCode: string) => {
    return sendPost<OrganizationMemberInviteModel>(
        `/api/organization/invitation/reject?inviteCode=${inviteCode}`,
        null
    );
};

export const deleteOrganizationInvitation = (invitationId: string) => {
    return sendDelete<OrganizationMemberInviteModel>(`/api/organization/invitation/${invitationId}`);
};

export const createOrganizationInvite = (organizationId: string, request: InviteOrganizationMemberRequest) => {
    return sendPost<OrganizationMemberInviteModel>(`/api/organization/${organizationId}/invite`, request);
};

export const getOrganization = (organizationId: string) => {
    return sendGet<OrganizationModel>(`/api/organization/${organizationId}`);
};

export const getOrganizationMembers = (organizationId: string, search?: ISearchParams) => {
    const url = buildQueryString(search ?? {}, `/api/organization/${organizationId}/members`);
    return sendGet<OrganizationMemberModel[]>(url);
};

export const getJoinedOrganizations = () => {
    return sendGet<OrganizationModel[]>(`/api/organization/joined`);
};

export const getOrganizationMember = (organizationMemberId: string) => {
    return sendGet<OrganizationMemberModel>(`/api/organization/members/${organizationMemberId}`);
};

export const removeOrganizationMember = (organizationMemberId: string) => {
    return sendDelete<OrganizationMemberModel>(`/api/organization/members/${organizationMemberId}`);
};

export const updateOrganizationMember = (organizationMemberId: string, request: UpdateOrganizationMemberRequest) => {
    return sendPut<OrganizationMemberModel>(`/api/organization/members/${organizationMemberId}`, request);
};

export const getOrganizationInvites = (organizationId: string, search?: ISearchParams) => {
    const url = buildQueryString(search ?? {}, `/api/organization/${organizationId}/invite`);
    return sendGet<OrganizationMemberInviteModel[]>(url);
};

export const updateOrganization = (organizationId: string, request: UpdateOrganizationRequest) => {
    return sendPut<OrganizationModel>(`/api/organization/${organizationId}`, request);
};

export const deleteOrganization = (organizationId: string) => {
    return sendDelete<OrganizationModel>(`/api/organization/${organizationId}`);
};

export const getOrgAuthClients = (organizationId: string) => {
    return sendGet<OrganizationAuthClientModel[]>(`/api/organization/${organizationId}/authClients`);
};

export const getOrgAuthClient = (organizationId: string, authClientId: string) => {
    return sendGet<OrganizationAuthClientModel>(`/api/organization/${organizationId}/authClients/${authClientId}`);
};

export const updateOrgAuthClient = (organizationId: string, authClientId: string, request: UpdateAuthClientRequest) => {
    return sendPut<OrganizationAuthClientModel>(
        `/api/organization/${organizationId}/authClients/${authClientId}`,
        request
    );
};

export const createOrgAuthClient = (organizationId: string, request: UpdateAuthClientRequest) => {
    return sendPost<OrganizationAuthClientModel>(`/api/organization/${organizationId}/authClients`, request);
};

export const rotateOrgAuthClientSecret = (organizationId: string, authClientId: string) => {
    return sendPost<OrganizationAuthClientModel>(
        `/api/organization/${organizationId}/authClients/${authClientId}/rotateSecret`,
        null
    );
};

export const deleteOrgAuthClient = (organizationId: string, authClientId: string) => {
    return sendDelete<OrganizationAuthClientModel>(`/api/organization/${organizationId}/authClients/${authClientId}`);
};

export const getOrgDeviceTypes = (organizationId: string) => {
    return sendGet<DeviceTypeModel[]>(`/api/organization/${organizationId}/deviceTypes`);
};

export const updateOrgDeviceType = (organizationId: string, deviceTypeId: string, request: UpdateDeviceTypeRequest) => {
    return sendPut<DeviceTypeModel>(`/api/organization/${organizationId}/deviceTypes/${deviceTypeId}`, request);
};

export const createOrgDeviceType = (organizationId: string, request: UpdateDeviceTypeRequest) => {
    return sendPost<DeviceTypeModel>(`/api/organization/${organizationId}/deviceTypes`, request);
};

export const deleteOrgDeviceType = (organizationId: string, deviceTypeId: string) => {
    return sendDelete<DeviceTypeModel>(`/api/organization/${organizationId}/deviceTypes/${deviceTypeId}`);
};

export const createOrgDeviceTypeCustomControls = (
    organizationId: string,
    deviceTypeId: string,
    request: CreateDeviceTypeCustomControlsRequest
) => {
    return sendPost<CustomControlsModel>(
        `/api/organization/${organizationId}/deviceTypes/${deviceTypeId}/customControls`,
        request
    );
};

export const updateOrgDeviceTypeCustomControls = (
    organizationId: string,
    deviceTypeId: string,
    locale: string,
    request: UpdateDeviceTypeCustomControlsRequest
) => {
    return sendPut<CustomControlsModel>(
        `/api/organization/${organizationId}/deviceTypes/${deviceTypeId}/customControls?locale=${locale}`,
        request
    );
};

export const getOrgDeviceTypeClientListings = (organizationId: string, deviceTypeId: string) => {
    return sendGet<DeviceTypeClientListingModel[]>(
        `/api/organization/${organizationId}/deviceTypes/${deviceTypeId}/clientListings`
    );
};

export const createOrgDeviceTypeClientListings = (
    organizationId: string,
    deviceTypeId: string,
    request: CreateDeviceTypeClientListingRequest
) => {
    return sendPost<DeviceTypeClientListingModel>(
        `/api/organization/${organizationId}/deviceTypes/${deviceTypeId}/clientListings`,
        request
    );
};

export const updateOrgDeviceTypeClientListing = (
    organizationId: string,
    deviceTypeId: string,
    request: UpdateDeviceTypeListingsModel
) => {
    return sendPut<DeviceTypeClientListingModel>(
        `/api/organization/${organizationId}/deviceTypes/${deviceTypeId}/clientListings`,
        request
    );
};

export const getOrgManagedLicenseKeys = (organizationId: string, search?: ISearchParams) => {
    const url = buildQueryString(search ?? {}, `/api/organization/${organizationId}/managedLicenseKeys`);
    return sendGet<LicenseKeyModel[]>(url);
};

export const getOrgManagedLicenseKeysAvailable = (organizationId: string, search?: ISearchParams) => {
    const url = buildQueryString(search ?? {}, `/api/organization/${organizationId}/managedLicenseKeys/available`);
    return sendGet<LicenseKeyModel[]>(url);
};

export const getOrgManagedLicenseKeysUsed = (organizationId: string, search?: ISearchParams) => {
    const url = buildQueryString(search ?? {}, `/api/organization/${organizationId}/managedLicenseKeys/used`);
    return sendGet<LicenseKeyModel[]>(url);
};

export const getOrgManagedOrgsAll = (organizationId: string, search?: ISearchParams) => {
    const url = buildQueryString(search ?? {}, `/api/organization/${organizationId}/managedOrganizations`);
    return sendGet<OrganizationModel[]>(url);
};

export const createManagedOrg = (organizationId: string, request: NewOrganizationWithKeyRequest) => {
    return sendPost<OrganizationModel>(`/api/organization/${organizationId}/managedOrganizations`, request);
};

export const getBillingLinkForManagedOrg = (organizationId: string, managedOrgId: string) => {
    return sendGet<OrganizationBillingLink>(
        `/api/organization/${organizationId}/managedOrganizations/${managedOrgId}/billing/link`
    );
};

export const getOrgProducts = (organizationId: string, locale: string, search?: ISearchParams) => {
    const url = buildQueryString(search ?? {}, `/api/organization/${organizationId}/products`, locale);
    return sendGet<OrganizationPrivateProductModel[]>(url);
};

export const getOrgProductLink = (organizationId: string, productId: string) => {
    return sendGet<OrganizationBillingLink>(`/api/organization/${organizationId}/products/${productId}/link`);
};

export const getUserOrganizationMembership = (organizationId: string) => {
    return sendGet<OrganizationMemberModel>(`/api/organization/${organizationId}/members/me`);
};

export const getUserOrganizationMemberships = () => {
    return sendGet<OrganizationMemberDetailsModel[]>(`/api/organization/memberships`);
};

export const getTrialUpgradeLink = (organizationId: string, trialId: string) => {
    return sendGet<OrganizationBillingLink>(`/api/organization/${organizationId}/trial/${trialId}/upgradeLink`);
};

export const getTrialUpgradeLinkForProduct = (organizationId: string, trialId: string, productId: string) => {
    return sendGet<OrganizationBillingLink>(
        `/api/organization/${organizationId}/trial/${trialId}/upgradeLink/product/${productId}`
    );
};

export const getBillingLink = (organizationId: string) => {
    return sendGet<OrganizationBillingLink>(`/api/organization/${organizationId}/billing/link`);
};

export const getBillingLinkForOrgByProduct = (organizationId: string, productId: string) => {
    return sendGet<OrganizationBillingLink>(`/api/organization/${organizationId}/billing/link/product/${productId}`);
};

export const getPublicProducts = () => {
    return sendGet<ProductModel[]>(`/api/License/products/public`);
};

export const postNewOrgActivateLicenseKey = (request: NewOrganizationWithKeyRequest) => {
    return sendPost<OrganizationModel>(`/api/License/activate`, request);
};

export const postExistingOrgActivateLicenseKey = (organizationId: string, request: ActivateLicenseKeyRequest) => {
    return sendPost<OrganizationModel>(`/api/License/activate/${organizationId}`, request);
};

export const validateLicenseKey = (licenseKey: string) => {
    return sendGet<LicenseKeyCheckResponse>(`/api/License/validate/${licenseKey}`);
};

// voice and camera models
export const getAllVoiceModelReferences = () => {
    return sendGet<VoiceModelReferenceModel[]>(`/api/ModelReference/voice`);
};
export const getVoiceModelReferenceById = (modelId: string) => {
    return sendGet<VoiceModelReferenceModel>(`/api/ModelReference/voice/${modelId}`);
};
export const getVoiceModelReferenceForLocale = (locale: string, fileFormat?: string) => {
    return sendGet<VoiceModelReferenceModel>(`/api/ModelReference/voice/locale/${locale}?fileFormat=${fileFormat}`);
};
export const getAllCameraModelReferences = () => {
    return sendGet<CameraModelReferenceModel>(`/api/ModelReference/vision`);
};
export const getCameraModelReferenceById = (modelId: string) => {
    return sendGet<CameraModelReferenceModel>(`/api/ModelReference/vision/${modelId}`);
};
